import axios, { AxiosError } from "axios";
import AuthClient from "./AuthClient";
import BookingType from "./enums/BookingType";
import {
  ApiResponse,
  handleAxiosError,
} from "./responses/AxiosResponseHandler";
import { parseOpenClubListingsResponse } from "./parseResponse/parseOpenClubListingsResponse";
import { OpenClubListing } from "../types";

const inFlightOpenClubListingsRequests = new Map<string, Promise<ApiResponse<OpenClubListing[]> | undefined>>();

interface QueryParameters {
  City: string;
  PartyDate: Date;
  BookingType: BookingType;
}

class OpenClubListingsApi {
  private authClient: AuthClient;

  constructor(authClient: AuthClient) {
    this.authClient = authClient;
  }

  public async getOpenClubListings(
    city: string,
    partyDate: Date,
    bookingType: BookingType
  ): Promise<ApiResponse<OpenClubListing[]> | undefined> {
    const requestKey = `${city}|${new Date(partyDate).toISOString()}|${bookingType}`;
    const existingRequest = inFlightOpenClubListingsRequests.get(requestKey);

    if (existingRequest) {
      return existingRequest;
    }

    const request = this.fetchOpenClubListings(city, partyDate, bookingType);
    inFlightOpenClubListingsRequests.set(requestKey, request);

    try {
      return await request;
    } finally {
      inFlightOpenClubListingsRequests.delete(requestKey);
    }
  }

  private async fetchOpenClubListings(
    city: string,
    partyDate: Date,
    bookingType: BookingType
  ): Promise<ApiResponse<OpenClubListing[]> | undefined> {
    try {
      const authenticationResponse = this.authClient.getAuthenticationResponse();
      if (authenticationResponse == null) {
        return undefined;
      }

      const queryParams: QueryParameters = {
        City: city,
        PartyDate: partyDate,
        BookingType: bookingType,
      };

      const response = await axios.get(
        `${this.authClient.getApiRoot()}/Clubs/OpenClubListings`,
        {
          params: queryParams,
          headers: {
            Authorization: `Bearer ${authenticationResponse.token}`,
          },
        }
      );

      return {
        statusCode: response.status,
        errorMessage: "",
        data: parseOpenClubListingsResponse(response.data as string[]).openClubListings ?? [],
      };
    } catch (error) {
      const errorResponse = handleAxiosError<OpenClubListing[]>(
        error as AxiosError
      );

      return {
        statusCode: errorResponse.statusCode,
        errorMessage: errorResponse.errorMessage,
        data: errorResponse.data,
      };
    }
  }
}

export default OpenClubListingsApi;
